import { create } from 'zustand';
import { format } from 'date-fns';
import type { LessonOverride, LessonSession } from '@court-sync/shared';
import type {
  OwnerSchedulePendingBooking,
  OwnerScheduleEnrollmentBooking,
  RemovedFromSessionEnrollmentRef,
  ReportedAbsenceSessionRef,
} from '@court-sync/shared';
import { lessonOverrideService } from '../services/lessonOverride';
import { lessonSessionsService } from '../services/lessonSessions';
import { ownerScheduleService } from '../services/ownerSchedule';
import { CACHE_TTL, isCacheValid } from './types';

interface DateRangeYmd {
  fromYmd: string;
  toYmd: string;
}

function toYmd(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function monthKeysBetween(start: Date, end: Date): string[] {
  const keys: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    keys.push(format(cursor, 'yyyy-MM'));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return keys;
}

function getDefaultCalendarRange(): { start: Date; end: Date } {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    end: new Date(now.getFullYear(), now.getMonth() + 3, 0),
  };
}

function unionRanges(ranges: DateRangeYmd[]): DateRangeYmd | null {
  if (ranges.length === 0) return null;
  let fromYmd = ranges[0]!.fromYmd;
  let toYmdVal = ranges[0]!.toYmd;
  for (const range of ranges.slice(1)) {
    if (range.fromYmd < fromYmd) fromYmd = range.fromYmd;
    if (range.toYmd > toYmdVal) toYmdVal = range.toYmd;
  }
  return { fromYmd, toYmd: toYmdVal };
}

function mergeSessionsForRange(
  prev: LessonSession[],
  incoming: LessonSession[],
  fromYmd: string,
  toYmd: string
): LessonSession[] {
  const outside = prev.filter((session) => {
    const ymd = session.sessionDate.replace(/T.*/, '').slice(0, 10);
    return ymd < fromYmd || ymd > toYmd;
  });
  return [...outside, ...incoming];
}

function mergeOverridesForRange(
  prev: LessonOverride[],
  incoming: LessonOverride[],
  fromYmd: string,
  toYmd: string,
): LessonOverride[] {
  const outside = prev.filter((override) => {
    const ymd = String(override.originalDate).replace(/T.*/, '').slice(0, 10);
    return ymd < fromYmd || ymd > toYmd;
  });
  return [...outside, ...incoming];
}

interface ApplyOwnerScheduleOptions {
  updateRangeData: boolean;
  updateBadgeData: boolean;
}

function applyOwnerScheduleToState(
  state: LessonCalendarState,
  academyId: string,
  fromYmd: string,
  endYmd: string,
  data: NonNullable<Awaited<ReturnType<typeof ownerScheduleService.getOwnerSchedule>>>,
  monthKeys: string[],
  options: ApplyOwnerScheduleOptions,
): Partial<LessonCalendarState> {
  const result: Partial<LessonCalendarState> = {
    academyId,
    fetchError: null,
  };

  if (options.updateBadgeData) {
    result.makeupSessions = data.makeupSessions;
    result.schedulePendingBookings = data.schedulePendingBookings;
    result.scheduleEnrollmentBookings = data.scheduleEnrollmentBookings;
    result.removedFromSessionEnrollments = data.removedFromSessionEnrollments;
    result.reportedAbsenceSessions = data.reportedAbsences;
    result.badgeLastFetchedAt = Date.now();
  }

  if (options.updateRangeData) {
    const cacheKey = rangeCacheKey(fromYmd, endYmd);
    const nextLoadedRanges = [...state.loadedRanges];
    const alreadyLoaded = nextLoadedRanges.some(
      (range) => range.fromYmd === fromYmd && range.toYmd === endYmd,
    );
    if (!alreadyLoaded) {
      nextLoadedRanges.push({ fromYmd, toYmd: endYmd });
    }
    const nextFetchedMonths = new Set(state.fetchedOverrideMonths);
    monthKeys.forEach((key) => nextFetchedMonths.add(key));

    result.lessonSessions = mergeSessionsForRange(
      state.lessonSessions,
      data.sessions,
      fromYmd,
      endYmd,
    );
    result.overrides = mergeOverridesForRange(
      state.overrides,
      data.overrides as unknown as LessonOverride[],
      fromYmd,
      endYmd,
    );
    result.loadedRanges = nextLoadedRanges;
    result.rangeLastFetchedAt = { ...state.rangeLastFetchedAt, [cacheKey]: Date.now() };
    result.fetchedOverrideMonths = nextFetchedMonths;
  }

  return result;
}

interface LessonCalendarState {
  academyId: string | null;
  lessonSessions: LessonSession[];
  overrides: LessonOverride[];
  makeupSessions: Array<{ lessonId: string; date: string }>;
  schedulePendingBookings: OwnerSchedulePendingBooking[];
  scheduleEnrollmentBookings: OwnerScheduleEnrollmentBooking[];
  removedFromSessionEnrollments: RemovedFromSessionEnrollmentRef[];
  reportedAbsenceSessions: ReportedAbsenceSessionRef[];
  loadedRanges: DateRangeYmd[];
  rangeLastFetchedAt: Record<string, number>;
  badgeLastFetchedAt: number | null;
  fetchedOverrideMonths: Set<string>;
  invalidationVersion: number;
  fetchError: string | null;

  resetForAcademy: (academyId: string) => void;
  clear: () => void;
  fetchForRange: (academyId: string, start: Date, end: Date) => Promise<void>;
  fetchOwnerScheduleForRange: (
    academyId: string,
    start: Date,
    end: Date,
    options?: { force?: boolean },
  ) => Promise<void>;
  refreshAfterLessonMutation: (academyId: string) => Promise<void>;
  refreshOwnerScheduleAfterMutation: (academyId: string) => Promise<void>;
  invalidate: () => void;
}

const initialState = {
  academyId: null as string | null,
  lessonSessions: [] as LessonSession[],
  overrides: [] as LessonOverride[],
  makeupSessions: [] as Array<{ lessonId: string; date: string }>,
  schedulePendingBookings: [] as OwnerSchedulePendingBooking[],
  scheduleEnrollmentBookings: [] as OwnerScheduleEnrollmentBooking[],
  removedFromSessionEnrollments: [] as RemovedFromSessionEnrollmentRef[],
  reportedAbsenceSessions: [] as ReportedAbsenceSessionRef[],
  loadedRanges: [] as DateRangeYmd[],
  rangeLastFetchedAt: {} as Record<string, number>,
  badgeLastFetchedAt: null as number | null,
  fetchedOverrideMonths: new Set<string>(),
  invalidationVersion: 0,
  fetchError: null as string | null,
};

function rangeCacheKey(fromYmd: string, toYmd: string): string {
  return `${fromYmd}|${toYmd}`;
}

export const useLessonCalendarStore = create<LessonCalendarState>((set, get) => ({
  ...initialState,

  resetForAcademy: (academyId) => {
    const { academyId: currentAcademyId } = get();
    if (currentAcademyId === academyId) return;
    set({
      ...initialState,
      academyId,
      fetchedOverrideMonths: new Set<string>(),
    });
  },

  clear: () => {
    set({
      ...initialState,
      fetchedOverrideMonths: new Set<string>(),
    });
  },

  fetchForRange: async (academyId, start, end) => {
    const fromYmd = toYmd(start);
    const endYmd = toYmd(end);
    const monthKeys = monthKeysBetween(start, end);
    const stateBeforeFetch = get();
    if (stateBeforeFetch.academyId !== academyId) {
      get().resetForAcademy(academyId);
    }

    const newMonths = monthKeys.filter((key) => !get().fetchedOverrideMonths.has(key));
    const cacheKey = rangeCacheKey(fromYmd, endYmd);
    const rangeAlreadyLoaded = get().loadedRanges.some(
      (range) => range.fromYmd === fromYmd && range.toYmd === endYmd
    );
    const rangeFetchedAt = get().rangeLastFetchedAt[cacheKey];
    const rangeCacheFresh =
      rangeAlreadyLoaded &&
      get().academyId === academyId &&
      isCacheValid(rangeFetchedAt, CACHE_TTL.MEDIUM);

    if (rangeCacheFresh && newMonths.length === 0) {
      return;
    }

    try {
      const sessionsPromise = rangeCacheFresh
        ? Promise.resolve([] as LessonSession[])
        : lessonSessionsService.listByAcademyAndDateRange(academyId, fromYmd, endYmd);

      if (newMonths.length > 0) {
        const data = await lessonOverrideService.getOverrides({
          academyId,
          fromDate: fromYmd,
          toDate: endYmd,
        });
        const nextFetchedMonths = new Set(get().fetchedOverrideMonths);
        newMonths.forEach((key) => nextFetchedMonths.add(key));
        set((state) => {
          const outside = state.overrides.filter((override) => {
            const month = format(new Date(override.originalDate), 'yyyy-MM');
            return !newMonths.includes(month);
          });
          return {
            overrides: [...outside, ...(data as unknown as LessonOverride[])],
            fetchedOverrideMonths: nextFetchedMonths,
            fetchError: null,
          };
        });
      }

      const sessions = await sessionsPromise;
      set((state) => {
        const nextLoadedRanges = [...state.loadedRanges];
        const alreadyLoaded = nextLoadedRanges.some(
          (range) => range.fromYmd === fromYmd && range.toYmd === endYmd
        );
        if (!alreadyLoaded) {
          nextLoadedRanges.push({ fromYmd, toYmd: endYmd });
        }
        const nextRangeLastFetchedAt = { ...state.rangeLastFetchedAt };
        if (!rangeCacheFresh) {
          nextRangeLastFetchedAt[cacheKey] = Date.now();
        }
        return {
          academyId,
          lessonSessions: rangeCacheFresh
            ? state.lessonSessions
            : mergeSessionsForRange(state.lessonSessions, sessions, fromYmd, endYmd),
          loadedRanges: nextLoadedRanges,
          rangeLastFetchedAt: nextRangeLastFetchedAt,
          fetchError: null,
        };
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load calendar sessions';
      set({ fetchError: message });
    }
  },

  fetchOwnerScheduleForRange: async (academyId, start, end, options) => {
    const fromYmd = toYmd(start);
    const endYmd = toYmd(end);
    const monthKeys = monthKeysBetween(start, end);
    const stateBeforeFetch = get();
    if (stateBeforeFetch.academyId !== academyId) {
      get().resetForAcademy(academyId);
    }

    const cacheKey = rangeCacheKey(fromYmd, endYmd);
    const rangeAlreadyLoaded = get().loadedRanges.some(
      (range) => range.fromYmd === fromYmd && range.toYmd === endYmd
    );
    const rangeFetchedAt = get().rangeLastFetchedAt[cacheKey];
    const rangeCacheFresh =
      !options?.force &&
      rangeAlreadyLoaded &&
      get().academyId === academyId &&
      isCacheValid(rangeFetchedAt, CACHE_TTL.MEDIUM);
    const badgeCacheFresh =
      !options?.force &&
      get().academyId === academyId &&
      isCacheValid(get().badgeLastFetchedAt, CACHE_TTL.SHORT);

    if (rangeCacheFresh && badgeCacheFresh) {
      return;
    }

    try {
      const data = await ownerScheduleService.getOwnerSchedule(
        academyId,
        fromYmd,
        endYmd,
        { bypassCache: options?.force },
      );
      if (!data) {
        set({ fetchError: 'Failed to load owner schedule' });
        return;
      }
      set((state) =>
        applyOwnerScheduleToState(state, academyId, fromYmd, endYmd, data, monthKeys, {
          updateRangeData: !rangeCacheFresh,
          updateBadgeData: !badgeCacheFresh,
        }),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load owner schedule';
      set({ fetchError: message });
    }
  },

  refreshAfterLessonMutation: async (academyId) => {
    const { loadedRanges, academyId: currentAcademyId } = get();
    if (currentAcademyId && currentAcademyId !== academyId) {
      get().resetForAcademy(academyId);
    }

    const defaultRange = getDefaultCalendarRange();
    const union = unionRanges([
      ...loadedRanges,
      { fromYmd: toYmd(defaultRange.start), toYmd: toYmd(defaultRange.end) },
    ]);
    if (!union) return;

    const start = new Date(`${union.fromYmd}T12:00:00`);
    const end = new Date(`${union.toYmd}T12:00:00`);

    set((state) => ({
      academyId,
      fetchedOverrideMonths: new Set<string>(),
      rangeLastFetchedAt: {},
      invalidationVersion: state.invalidationVersion + 1,
    }));

    try {
      const [overrides, sessions] = await Promise.all([
        lessonOverrideService.getOverrides({
          academyId,
          fromDate: union.fromYmd,
          toDate: union.toYmd,
        }),
        lessonSessionsService.listByAcademyAndDateRange(
          academyId,
          union.fromYmd,
          union.toYmd
        ),
      ]);

      const nextFetchedMonths = new Set(monthKeysBetween(start, end));
      set((state) => ({
        academyId,
        overrides: overrides as unknown as LessonOverride[],
        lessonSessions: mergeSessionsForRange(
          state.lessonSessions,
          sessions,
          union.fromYmd,
          union.toYmd
        ),
        fetchedOverrideMonths: nextFetchedMonths,
        fetchError: null,
      }));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to refresh calendar data';
      set({ fetchError: message });
    }
  },

  refreshOwnerScheduleAfterMutation: async (academyId) => {
    const { loadedRanges, academyId: currentAcademyId } = get();
    if (currentAcademyId && currentAcademyId !== academyId) {
      get().resetForAcademy(academyId);
    }

    const defaultRange = getDefaultCalendarRange();
    const union = unionRanges([
      ...loadedRanges,
      { fromYmd: toYmd(defaultRange.start), toYmd: toYmd(defaultRange.end) },
    ]);
    if (!union) return;

    const start = new Date(`${union.fromYmd}T12:00:00`);
    const end = new Date(`${union.toYmd}T12:00:00`);
    const monthKeys = monthKeysBetween(start, end);

    set((state) => ({
      academyId,
      fetchedOverrideMonths: new Set<string>(),
      rangeLastFetchedAt: {},
      badgeLastFetchedAt: null,
      invalidationVersion: state.invalidationVersion + 1,
    }));

    try {
      const data = await ownerScheduleService.getOwnerSchedule(
        academyId,
        union.fromYmd,
        union.toYmd,
        { bypassCache: true },
      );
      if (!data) {
        set({ fetchError: 'Failed to refresh owner schedule' });
        return;
      }
      set((state) =>
        applyOwnerScheduleToState(
          state,
          academyId,
          union.fromYmd,
          union.toYmd,
          data,
          monthKeys,
          { updateRangeData: true, updateBadgeData: true },
        ),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to refresh owner schedule';
      set({ fetchError: message });
    }
  },

  invalidate: () => {
    set((state) => ({
      invalidationVersion: state.invalidationVersion + 1,
    }));
  },
}));
